/* eslint-disable react-hooks/exhaustive-deps */
import { EditOutlined, DeleteOutlined, SyncOutlined } from "@ant-design/icons";
import {
  Table,
  Button,
  Space,
  Tag,
  Tooltip,
  Popconfirm,
  Typography,
  theme,
  Image,
  Badge,
  Grid
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import CreateEditSessionModal from "./CreateEditSessionModal";
import { showErrorNotification, showSuccessNotification } from "../../utils/notification";
import { clamp } from "../../utils/responsiveHelpers";

const { Text } = Typography;
const { useToken } = theme;
const { useBreakpoint } = Grid;

export const COURSE_SESSION_STATUS = Object.freeze({
  SESSION_UPCOMING: "SESSION_UPCOMING",
  SESSION_LIVE: "SESSION_LIVE",
  SESSION_ENDED_RECORDING_PENDING: "SESSION_ENDED_RECORDING_PENDING",
  SESSION_ENDED_RECORDING_AVAILABLE: "SESSION_ENDED_RECORDING_AVAILABLE"
});

const SESSION_STATUS_CONFIG = {
  [COURSE_SESSION_STATUS.SESSION_UPCOMING]: { label: "Upcoming", color: "blue" },
  [COURSE_SESSION_STATUS.SESSION_LIVE]: { label: "Live 🔴", color: "green" },
  [COURSE_SESSION_STATUS.SESSION_ENDED_RECORDING_PENDING]: {
    label: "Recording Pending",
    color: "orange"
  },
  [COURSE_SESSION_STATUS.SESSION_ENDED_RECORDING_AVAILABLE]: {
    label: "Recording Available",
    color: "purple"
  }
};

const CourseSessionsManagementDash = ({ userConfig, ROOT_URL }) => {
  const hasManagementAccess =
    userConfig?.courseSessions?.hasManagementAccess ||
    userConfig?.liveSessions?.hasManagementAccess ||
    false;
  const { token } = useToken();
  const screens = useBreakpoint();
  const isMobileView = screens.md === false;

  const BASE_URL = "admin/course-sessions";

  const [sessionsData, setSessionsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Fetch sessions list from admin API
   */
  const fetchSessions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${ROOT_URL}/${BASE_URL}/list`);
      setSessionsData(response.data?.sessionsList ?? []);
    } catch (error) {
      showErrorNotification(error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Create or update a session
   */
  const handleSaveSession = async (formValues) => {
    const isEditing = !!initialValues;
    const endpoint = isEditing ? `${BASE_URL}/update` : `${BASE_URL}/create`;

    const sessionParams = isEditing
      ? { ...formValues, uniquePublicId: initialValues.uniquePublicId }
      : formValues;
    const payload = { sessionParams };

    try {
      await axios.post(`${ROOT_URL}/${endpoint}`, payload);
      showSuccessNotification(`Session ${isEditing ? "updated" : "created"} successfully`);
      setIsModalOpen(false);
      setInitialValues(null);
      fetchSessions();
    } catch (error) {
      showErrorNotification(error);
    }
  };

  /**
   * Soft-delete a session
   */
  const deleteSession = async (uniquePublicId) => {
    try {
      await axios.post(`${ROOT_URL}/${BASE_URL}/delete`, { uniquePublicId });
      showSuccessNotification("Session deleted successfully");
      fetchSessions();
    } catch (error) {
      showErrorNotification(error);
    }
  };

  /**
   * Force re-fetch TP Streams metadata for a session (e.g. after the
   * thumbnail/poster was updated on the TP Streams side without the asset ID changing)
   */
  const refreshTpStreamMetadata = async (uniquePublicId) => {
    try {
      await axios.post(`${ROOT_URL}/${BASE_URL}/refresh-tp-stream-metadata`, { uniquePublicId });
      showSuccessNotification("TP Streams metadata refreshed successfully");
      fetchSessions();
    } catch (error) {
      showErrorNotification(error);
    }
  };

  const columns = [
    {
      title: "S No.",
      width: 70,
      render: (_, __, index) => index + 1
    },
    {
      title: "Thumbnail",
      dataIndex: "thumbnailUrl",
      width: 140,
      render: (thumbnailUrl) =>
        thumbnailUrl ? (
          <Image
            src={thumbnailUrl}
            alt="thumbnail"
            width={100}
            style={{ borderRadius: 4, objectFit: "cover" }}
          />
        ) : (
          <Text type="secondary">—</Text>
        )
    },
    {
      title: "Title, Instructor & Description",
      width: 240,
      render: (_, session) => (
        <div>
          <Text strong>{session.title}</Text>
          <br />
          <Text style={{ fontSize: 12, color: "#6366f1", fontWeight: 500 }}>
            👤 {session.instructorName || "—"}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {session.description}
          </Text>
        </div>
      )
    },
    {
      title: "Status",
      dataIndex: "sessionStatus",
      width: 160,
      render: (status) => {
        const statusConfig = SESSION_STATUS_CONFIG[status] ?? {
          label: status,
          color: "default"
        };
        return <Tag color={statusConfig.color}>{statusConfig.label}</Tag>;
      }
    },
    {
      title: "Schedule (Start - End)",
      width: 200,
      render: (_, session) => {
        if (!session.sessionStartDate) {
          return "—";
        }
        const formattedStart = dayjs(session.sessionStartDate).format("DD MMM YYYY, hh:mm A");
        const formattedEnd = session.sessionEndDate
          ? dayjs(session.sessionEndDate).format("hh:mm A")
          : "";
        return (
          <Text>
            {formattedStart} - {formattedEnd}
          </Text>
        );
      }
    },
    {
      title: "tpStreams Asset",
      dataIndex: "tpStreamsAssetId",
      width: 150,
      render: (tpStreamsAssetId) => tpStreamsAssetId ?? <Text type="secondary">—</Text>
    },
    {
      title: "Butterbars",
      dataIndex: "announcementBanners",
      width: 260,
      render: (announcementBanners) => {
        if (!announcementBanners || announcementBanners.length === 0) {
          return <Text type="secondary">—</Text>;
        }

        const phaseLabels = ["Upcoming", "Live", "Recording Pending"];
        const phaseColors = ["blue", "green", "orange"];

        return (
          <Space direction="vertical" size="small" style={{ fontSize: 12 }}>
            {announcementBanners.map(({ phaseIndex, message, priority, isActive }) => (
              <div key={phaseIndex}>
                <Text strong style={{ color: phaseColors[phaseIndex] ?? "gray" }}>
                  {phaseLabels[phaseIndex] ?? `Phase ${phaseIndex}`}:
                </Text>{" "}
                {message || <Text type="secondary">—</Text>}
                {priority !== null && priority !== undefined && (
                  <Badge
                    count={`P${priority}`}
                    style={{ backgroundColor: token.colorSuccess, marginLeft: 4 }}
                  />
                )}{" "}
                {isActive ? (
                  <Text type="success" style={{ fontSize: 10 }}>
                    ● Active
                  </Text>
                ) : (
                  <Text type="secondary" style={{ fontSize: 10 }}>
                    ○ Inactive
                  </Text>
                )}
              </div>
            ))}
          </Space>
        );
      }
    },
    {
      title: "Stats",
      width: 110,
      render: (_, session) => (
        <Space direction="vertical" size={0}>
          <Text>👍 {session.stats?.likesCount ?? 0}</Text>
          <Text>👎 {session.stats?.dislikesCount ?? 0}</Text>
        </Space>
      )
    },
    {
      title: "Audit",
      width: 160,
      render: (_, session) => (
        <Space direction="vertical" size={0} style={{ fontSize: 12 }}>
          <Text type="secondary">Created: {session.createdBy ?? "—"}</Text>
          <Text type="secondary">Updated: {session.lastUpdatedBy ?? "—"}</Text>
        </Space>
      )
    },
    {
      title: "Actions",
      fixed: isMobileView ? undefined : "right",
      width: 140,
      render: (_, session) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              disabled={!hasManagementAccess}
              onClick={() => {
                setInitialValues(session);
                setIsModalOpen(true);
              }}
            />
          </Tooltip>

          <Tooltip
            title={
              session.tpStreamsAssetId
                ? "Refresh TP Streams Metadata"
                : "No TP Streams Asset ID set"
            }
          >
            <Button
              type="text"
              icon={<SyncOutlined />}
              disabled={!hasManagementAccess || !session.tpStreamsAssetId}
              onClick={() => refreshTpStreamMetadata(session.uniquePublicId)}
            />
          </Tooltip>

          <Popconfirm
            title="Delete this session?"
            description="This action cannot be undone."
            onConfirm={() => deleteSession(session.uniquePublicId)}
            disabled={!hasManagementAccess}
            placement="left"
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} disabled={!hasManagementAccess} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  useEffect(() => {
    fetchSessions();
  }, []);

  return (
    <div style={{ padding: clamp(12, 24) }}>
      <div
        style={{
          background: token.colorBgContainer,
          padding: 20,
          borderRadius: 8,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 16,
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: "12px"
          }}
        >
          <div>
            <h2 style={{ margin: 0 }}>Course Sessions</h2>
            <Text type="secondary">
              Manage prime interactive sessions — upcoming, live, and recorded
            </Text>
          </div>

          {hasManagementAccess && (
            <Button
              type="primary"
              size="large"
              onClick={() => {
                setInitialValues(null);
                setIsModalOpen(true);
              }}
            >
              + New Session
            </Button>
          )}
        </div>

        <Table
          columns={columns}
          dataSource={sessionsData}
          loading={loading}
          bordered
          rowKey="uniquePublicId"
          pagination={{ pageSize: 10 }}
          scroll={{ x: "max-content" }}
        />
      </div>

      {isModalOpen && (
        <CreateEditSessionModal
          initialValues={initialValues}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          onSubmit={handleSaveSession}
          ROOT_URL={ROOT_URL}
        />
      )}
    </div>
  );
};

export default CourseSessionsManagementDash;
